import React, { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Grid, List, ListItem, makeStyles, TextField, Typography } from "@material-ui/core"
import { StandardLoadingButton } from "shared/Components/Loading/LoadingButton"
import { Logger } from "shared/Helpers/logging"
import { LocalizedStrict } from "shared/Modules/Localization/Components/Localized"
import { useApiCall } from "shared/Modules/Query/useApiCall"
import { useToken } from "shared/Modules/Login/useToken"
import { getUser } from "shared/Modules/User/userActions"
import { selectUserIsPrivate, selectUserLocation } from "shared/Modules/User/userSelectors"
import { ChosenLocation, Location } from "../locationTypes"
import { filterSearch, toSelection } from "../locationLib"
import { setUserLocation } from "../locationApi"
import { CompanyLocationChoice, PublicLocationChoice } from "./LocationChoice"
import { useEnvironment } from "shared/Modules/Environment/envHooks"

const useStyles = makeStyles((theme) => ({
    searchField: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    locationList: {
        paddingBottom: theme.spacing(3),
    },
    noResults: {
        padding: theme.spacing(3, 2),
        textAlign: "center",
    },
    confirmArea: {
        position: "sticky",
        bottom: 0,
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}))

type LocationListProps = Readonly<{
    locations: Location[]
    onSelect: () => void
    // true: tapping a location only marks it, it is saved with a confirm button (onboarding flow)
    confirm?: boolean
}>

export function LocationList({ locations, onSelect, confirm = false }: LocationListProps) {
    const classes = useStyles()

    const userLocation = useSelector(selectUserLocation)
    const isPrivateUser = useSelector(selectUserIsPrivate)
    const dispatch = useDispatch()

    const [search, setSearch] = useState("")
    const [pending, setPending] = useState<ChosenLocation>()
    const savedSelection = useMemo(() => toSelection(userLocation), [userLocation])
    const selection = confirm ? (pending ?? savedSelection) : savedSelection
    const searchRegex = useMemo(() => search ? new RegExp(`(${search})`, "i") : undefined, [search])
    const filteredLocations = useMemo(() => filterSearch(locations, search), [locations, search, filterSearch])

    const token = useToken()
    const { currentEnv } = useEnvironment()
    const { loading: isSavingLocation, callForAction: saveLocation, handleCallError } = useApiCall(setUserLocation)

    const logger = new Logger("locations")

    function saveSelection(locationId: number | undefined, kitchenId: number | undefined) {
        saveLocation(token, currentEnv, locationId, kitchenId)
            .then((_response) => {
                onSelect()
                dispatch(getUser())
            })
            .catch((e) => handleCallError(e, "saving location choice"))
    }

    function handleSelect(locationId: number | undefined, kitchenId: number | undefined) {
        if (confirm) setPending({ locationId, kitchenId })
        else saveSelection(locationId, kitchenId)
    }

    function handleConfirm() {
        if (!selection) return
        // Nothing changed (e.g. came back to this step): no need to save the same location again
        if (!pending && savedSelection) {
            onSelect()
            return
        }
        saveSelection(selection.locationId, selection.kitchenId)
    }

    return (
        <Grid container direction="column" wrap="nowrap">
            <Grid item className={classes.searchField}>
                <LocalizedStrict id="choose-location-search-field" attrs={{ label: true }}>
                    <TextField
                        variant="outlined"
                        label="Søg kantine / restaurant (navn, adresse...)"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        fullWidth
                    />
                </LocalizedStrict>
            </Grid>
            {filteredLocations.length === 0 && (
                <Grid item className={classes.noResults}>
                    <LocalizedStrict id="choose-location-no-results">
                        <Typography variant="body2" color="textSecondary">
                            Ingen lokationer matcher din søgning.
                        </Typography>
                    </LocalizedStrict>
                </Grid>
            )}
            <Grid item className={classes.locationList}>
                <List>
                    {filteredLocations.map((location, index, array) => (
                        <ListItem key={index} disableGutters divider={index < array.length - 1}>
                            {isPrivateUser ? (
                                <PublicLocationChoice
                                    location={location}
                                    search={searchRegex}
                                    loading={isSavingLocation}
                                    selection={selection}
                                    onSelect={handleSelect}
                                    variant={confirm ? "card" : "button"}
                                />
                            ) : (
                                <CompanyLocationChoice
                                    location={location}
                                    search={searchRegex}
                                    loading={isSavingLocation}
                                    selection={selection}
                                    onSelect={handleSelect}
                                    variant={confirm ? "card" : "button"}
                                />
                            )}
                        </ListItem>
                    ))}
                </List>
            </Grid>
            {confirm && (
                <Grid item className={classes.confirmArea}>
                    <LocalizedStrict id="choose-location-confirm-button" attrs={{ loadingLabel: true }}>
                        <StandardLoadingButton
                            variant="contained"
                            color="black"
                            size="large"
                            fullWidth
                            disabled={!selection || filteredLocations.length === 0}
                            loading={isSavingLocation}
                            loadingLabel="Gemmer valg..."
                            onClick={handleConfirm}
                        >
                            Bekræft lokation
                        </StandardLoadingButton>
                    </LocalizedStrict>
                </Grid>
            )}
        </Grid>
    )
}
